const EventEmitter = require('eventemitter3');

const BattleConfig = require('../constants/BattleConfig');
const BattleCalc = require('./BattleCalc');
const { logError } = require('./logger');

// standard
const Move = require('./abilities/Move');
const MeleeAttack = require('./abilities/MeleeAttack');

// alchemy
const PoisonWeapon = require('./abilities/alchemy/PoisonWeapon');
const BurningHands = require('./abilities/alchemy/BurningHands');
const MolotovCocktail = require('./abilities/alchemy/MolotovCocktail');
const IceNine = require('./abilities/alchemy/IceNine');
const Fumigate = require('./abilities/alchemy/Fumigate');
const Lightning = require('./abilities/alchemy/Lightning');
const RoadToNowhere = require('./abilities/alchemy/RoadToNowhere');

// armor use
const ShieldBlock = require('./abilities/armor_use/ShieldBlock');
const Bash = require('./abilities/armor_use/Bash');
const Suture = require('./abilities/armor_use/Suture');

// bow use
const ArrowShot = require('./abilities/bow_use/ArrowShot');
const LegShot = require('./abilities/bow_use/LegShot');
const RazorShot = require('./abilities/bow_use/RazorShot');
const BluntArrow = require('./abilities/bow_use/BluntArrow');
const ChainPull = require('./abilities/bow_use/ChainPull');
const MultiShot = require('./abilities/bow_use/MultiShot');

// conjuration
const VoidSpike = require('./abilities/conjuration/VoidSpike');
const SummonDwarf = require('./abilities/conjuration/SummonDwarf').default;
const SummonElf = require('./abilities/conjuration/SummonElf').default;
const SummonFaerie = require('./abilities/conjuration/SummonFaerie').default;
const SummonOption = require('./abilities/conjuration/SummonOption');
const Teleport = require('./abilities/conjuration/Teleport');

// illusion
const SleightOfHand = require('./abilities/illusion/SleightOfHand');
const AngelMask = require('./abilities/illusion/AngelMask');
const PsycheOut = require('./abilities/illusion/PsycheOut');
const InfectMind = require('./abilities/illusion/InfectMind');
const DevilMask = require('./abilities/illusion/DevilMask');
const FauxSelf = require('./abilities/illusion/FauxSelf').default;

// melee combat
const Slash = require('./abilities/melee_combat/Slash');
const Slam = require('./abilities/melee_combat/Slam');
const Taunt = require('./abilities/melee_combat/Taunt');
const ArmorBreak = require('./abilities/melee_combat/ArmorBreak');
const Roundhouse = require('./abilities/melee_combat/Roundhouse');
const MortalStrike = require('./abilities/melee_combat/MortalStrike');
const SkyHammer = require('./abilities/melee_combat/SkyHammer');

// movement
const Adrenaline = require('./abilities/movement/Adrenaline');
const Leap = require('./abilities/movement/Leap');
const StanceDancing = require('./abilities/movement/StanceDancing');
const Tussle = require('./abilities/movement/Tussle');

// spirit
const HealingPrayer = require('./abilities/spirit/HealingPrayer');
const MysticFocus = require('./abilities/spirit/MysticFocus');
const SendMana = require('./abilities/spirit/SendMana');
const Bless = require('./abilities/spirit/Bless');
const Sanctuary = require('./abilities/spirit/Sanctuary');
const HolyForce = require('./abilities/spirit/HolyForce');

const getBlessedFactor = (actor) => {
  let blessedFactor = 1.0;
  const blessed = BattleCalc.getCondition(actor, 'blessed');
  if (blessed) {
    blessedFactor -= blessed.potency / 100;
  }
  return blessedFactor;
};

export default function AbilityExecutor(battleState) {
  try {
    this.eventEmitter = new EventEmitter();

    let ABILITIES = {
      // standard
      move: new Move(battleState),
      melee_attack: new MeleeAttack(battleState),

      // alchemy
      poison_weapon: new PoisonWeapon(battleState),
      burning_hands: new BurningHands(battleState),
      molotov_cocktail: new MolotovCocktail(battleState),
      ice_nine: new IceNine(battleState),
      fumigate: new Fumigate(battleState),
      lightning: new Lightning(battleState),
      road_to_nowhere: new RoadToNowhere(battleState),

      // armor use
      shield_block: new ShieldBlock(battleState),
      bash: new Bash(battleState),
      suture: new Suture(battleState),

      // bow use
      arrow_shot: new ArrowShot(battleState),
      leg_shot: new LegShot(battleState),
      razor_shot: new RazorShot(battleState),
      blunt_arrow: new BluntArrow(battleState),
      chain_pull: new ChainPull(battleState),
      multi_shot: new MultiShot(battleState),

      // conjuration
      void_spike: new VoidSpike(battleState),
      summon_dwarf: new SummonDwarf(battleState),
      summon_elf: new SummonElf(battleState),
      summon_faerie: new SummonFaerie(battleState),
      summon_option: new SummonOption(battleState),
      teleport: new Teleport(battleState),

      // illusion
      sleight_of_hand: new SleightOfHand(battleState),
      angel_mask: new AngelMask(battleState),
      psyche_out: new PsycheOut(battleState),
      infect_mind: new InfectMind(battleState),
      devil_mask: new DevilMask(battleState),
      faux_self: new FauxSelf(battleState),

      // melee combat
      slash: new Slash(battleState),
      slam: new Slam(battleState),
      taunt: new Taunt(battleState),
      armor_break: new ArmorBreak(battleState),
      roundhouse: new Roundhouse(battleState),
      mortal_strike: new MortalStrike(battleState),
      sky_hammer: new SkyHammer(battleState),

      // movement
      adrenaline: new Adrenaline(battleState),
      leap: new Leap(battleState),
      stance_dancing: new StanceDancing(battleState),
      tussle: new Tussle(battleState),

      // spirit
      healing_prayer: new HealingPrayer(battleState),
      mystic_focus: new MysticFocus(battleState),
      send_mana: new SendMana(battleState),
      bless: new Bless(battleState),
      sanctuary: new Sanctuary(battleState),
      holy_force: new HolyForce(battleState),
    };

    const executionIsLegal = (data = {}) => {
      try {
        const actor = battleState.allUnits[data.actorId];
        const victim = battleState.allPieces[data.victimId];
        const ability = ABILITIES[data.abilityHandle];
        const abilityState = BattleCalc.getEquippedAbility(
          actor,
          data.abilityHandle
        );

        if (
          !actor ||
          !victim ||
          !actor.inPlay ||
          actor.dead ||
          (victim.unit && (victim.dead || !victim.inPlay)) ||
          !ability
        ) {
          data.legal = false;
          return data;
        }

        data.withinRange = withinRange(actor, victim, abilityState);
        data.statsSufficient = statsSufficient(actor, victim, abilityState);
        data.legal = ability.isLegal(actor, victim);
        return data;
      } catch (err) {
        logError(
          err,
          {
            module: 'AbilityExecutor',
            func: 'executionIsLegal',
            data: JSON.stringify(data),
          }
        );
      }
    };

    const withinRange = (actor, victim, abilityState) => {
      const minRange = abilityState.range.min;
      const maxRange = abilityState.range.max;

      if (victim === null) {
        return minRange === 0 && maxRange === 0;
      } else {
        const dist = BattleCalc.boardDistance(actor, victim);
        return dist >= minRange && dist <= maxRange;
      }
    };

    const statsSufficient = (actor, victim, abilityState) => {
      const blessedFactor = getBlessedFactor(actor);

      if (abilityState.handle === 'leap') {
        return (
          actor.ap >=
          BattleCalc.getLeapAPCostByDist(
            actor,
            BattleCalc.boardDistance(actor, victim)
          ) *
          blessedFactor
        );
      } else if (abilityState.handle === 'teleport') {
        return (
          actor.mp >=
          BattleCalc.getTeleportMPCostByDist(
            actor,
            BattleCalc.boardDistance(actor, victim)
          ) *
          blessedFactor
        );
      }

      return (
        (!abilityState.hpCost ||
          actor.hp >= abilityState.hpCost * blessedFactor) &&
        (!abilityState.apCost ||
          actor.ap >= abilityState.apCost * blessedFactor) &&
        (!abilityState.mpCost ||
          actor.mp >= abilityState.mpCost * blessedFactor)
      );
    };

    this.attemptIsLegal = (data = {}) => {
      try {
        if (this.done) {
          data.legal = false;
          return data;
        }

        const actor = battleState.allUnits[data.actorId];
        if (!actor) {
          data.legal = false;
          return data;
        }

        return executionIsLegal(data);
      } catch (err) {
        logError(err, {
          module: 'AbilityExecutor',
          func: 'attemptIsLegal',
          data,
          battleState,
        });
      }
    };

    this.exactStatCosts = (data) => {
      try {
        if (this.done) {
          return data;
        }

        const _actor = battleState.allPieces[data.actorId];
        const _victim = battleState.allPieces[data.victimId];
        const _abilityState = BattleCalc.getEquippedAbility(
          _actor,
          data.abilityHandle
        );

        const blocking = BattleCalc.getCondition(_actor, 'blocking');
        if (blocking) {
          battleState.expireCondition(blocking.uid);
        }

        const blessedFactor = BattleCalc.getBlessedFactor(_actor);

        if (_abilityState.hpCost) {
          data.hpCost = _abilityState.hpCost * blessedFactor;
          _actor.hp -= data.hpCost;
        }

        if (_abilityState.apCost) {
          if (_abilityState.handle === 'leap') {
            data.apCost =
              BattleCalc.getLeapAPCostByDist(
                _actor,
                BattleCalc.boardDistance(_actor, _victim)
              ) * blessedFactor;
          } else {
            data.apCost = _abilityState.apCost * blessedFactor;
          }
          _actor.ap -= data.apCost;
        }

        if (_abilityState.mpCost) {
          if (_abilityState.handle === 'teleport') {
            data.mpCost =
              BattleCalc.getTeleportMPCostByDist(
                _actor,
                BattleCalc.boardDistance(_actor, _victim)
              ) * blessedFactor;
          } else {
            data.mpCost = _abilityState.mpCost * blessedFactor;
          }
          _actor.mp -= data.mpCost;
        }

        if (_abilityState.handle === 'leap') {
          data.projectileTime = BattleCalc.projectileTime(
            BattleConfig.KNOCKBACK_SPEED,
            _actor,
            _victim
          );
        } else if (_abilityState.handle === 'molotov_cocktail') {
          data.projectileTime = BattleCalc.projectileTime(
            BattleConfig.THROWN_SPEED,
            _actor,
            _victim
          );
        }

        return data;
      } catch (err) {
        logError(err, {
          module: 'AbilityExecutor',
          func: 'exactStatCosts',
          data,
          battleState,
        });
      }
    };

    this.execute = (data = {}) => {
      try {
        if (this.done || !battleState) {
          return;
        }

        const _actor = battleState.allPieces[data.actorId];
        const _victim = battleState.allPieces[data.victimId];
        const _ability = ABILITIES[data.abilityHandle];

        if (!_actor) {
          return;
        }

        if (data.stance) {
          _ability.stance = data.stance;
        }

        return _ability.execute(_actor, _victim);
      } catch (err) {
        logError(
          err,
          {
            module: 'AbilityExecutor',
            func: 'execute',
            data: JSON.stringify(data),
          },

        );
      }
    };

    const onResult = (result) => {
      this.eventEmitter.emit('EXECUTION_RESULT', result);
    };

    const onProc = (procData) => {
      try {
        switch (procData.procHandle) {
          case 'thunder':
            ABILITIES.lightning.execute(procData.actor, procData.victim, true);
            break;

          case 'fire':
            ABILITIES.burning_hands.execute(
              procData.actor,
              procData.victim,
              true
            );
            break;

          case 'ice':
            ABILITIES.ice_nine.execute(procData.actor, procData.victim, true);
            break;
        }
      } catch (err) {
        logError(err, {
          module: 'AbilityExecutor',
          func: 'onProc',
          procData,
          battleState,
        });
      }
    };

    for (const ability of Object.values(ABILITIES)) {
      if (ability.eventEmitter) {
        ability.eventEmitter.on('result', onResult);
        ability.eventEmitter.on('proc', onProc);
      }
    }

    this.executeAngelMasks = () => {
      try {
        for (const unit of Object.values(battleState.allUnits || {})) {
          if (
            BattleCalc.getPassiveAbility(unit, 'angel_mask') &&
            unit.inPlay &&
            !unit.dead
          ) {
            ABILITIES.angel_mask.execute(unit);
          }
        }
      } catch (err) {
        logError(err, {
          module: 'AbilityExecutor',
          func: 'executeAngelMasks',
          battleState,
        });
      }
    };

    this.dispose = function () {
      try {
        this.done = true;

        this.eventEmitter = null;

        for (const prop in ABILITIES) {
          ABILITIES[prop].eventEmitter.removeAllListeners();
        }
        ABILITIES = null;
      } catch (err) {
        logError(err, {
          module: 'AbilityExecutor',
          func: 'dispose',
        });
      }
    };
  } catch (err) {
    logError(err, {
      module: 'AbilityExecutor',
      func: 'constructor',
      battleState,
    });
  }
};
